import React, { useState, useMemo, useCallback, useContext } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useField } from 'react-final-form';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import { Accordion, ContextMenu, Header3SemiBold, InputField, Table as BaseTable } from '@ubeya/shared-web/components';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';
import usePositionsGroups from '@ubeya/shared/hooks/account/usePositionsGroups';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { media } from '@ubeya/shared-web/style';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import privileges from '@ubeya/shared-web/privileges';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import { groupBaseWages, groupWages, wageCellFormatter } from '../../../utils/wages';
import { AccordionContent, RootContainer, RootWrapper } from '../styled';
import PositionRenderer from './PositionRenderer';
import WageActionRenderer from './WageActionRenderer';
import WageModal from './WageModal';
import PositionsGroupWageModal from './PositionsGroupWageModal';

const SmallCategory = styled(Header3SemiBold)`
  color: ${({ theme }) => theme.colors.black};
  padding: 4px 13px;
`;

const FieldsContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-gap: 10px;

  ${media.tablet`grid-template-columns: repeat(2, minmax(0, 1fr));`}
`;

const Table = styled(BaseTable)`
  .row.past {
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.grey3};
  }

  .row.future {
    color: ${({ theme }) => theme.colors.grey3};
  }
`;

const PositionsGroupsTitle = styled.div`
  width: 100%;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-top: 25px;
`;

const Wages = ({ positions: basePositions = [], payrollFieldsByEmployee }) => {
  const { t } = useContext(ConfigContext);
  const [editedWage, setEditedWage] = useState();
  const [editedPositionsGroupWage, setEditedPositionGroupWage] = useState();
  const { mappedPositions } = usePositions();
  const { isLoading, positionsGroups } = usePositionsGroups();
  const myPrivileges = useContext(PrivilegesContext);

  const hasAmendPayRatePrivilege = !!myPrivileges[privileges.AMEND_PAY_RATE];

  const positions = useMemo(() => basePositions.filter((id) => !mappedPositions?.[id]?.isDeleted), [
    basePositions,
    mappedPositions
  ]);

  const { data, handleDelete, handleAdd, handleEdit } = useArrayManagement('positionWages');
  const {
    data: baseWagesData,
    handleDelete: handleDeleteBaseWage,
    handleAdd: handleBaseWageAdd,
    handleEdit: handleBaseWageEdit
  } = useArrayManagement('wages');

  const {
    input: { value: positionsWages, onChange: onChangePositionsWages }
  } = useField('positionWages');

  const { dateFormat } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();
  const { account } = useAccountConfig();

  const { isPayRatesEnabled } = useContext(ConfigContext);

  const isFlatWage = account?.payrollConfig?.flatIsActive;

  const handleAddWage = useCallback((item) => {
    setEditedWage(item?.isBaseWage ? { isBaseWage: true } : item);
  }, []);

  const handleEditWage = useCallback((item) => setEditedWage(item), []);

  const handleDeleteWage = useCallback(
    (item) => (item?.isBaseWage ? handleDeleteBaseWage(item.id) : handleDelete(item.id)),
    [handleDelete, handleDeleteBaseWage]
  );

  const baseWageRow = useMemo(() => groupBaseWages(baseWagesData), [baseWagesData]);

  // The grouped row's top-level fields are the wage currently in effect; when the
  // history is empty or future-only there is no current base wage.
  const currentBaseWage = useMemo(() => (baseWageRow.id && !baseWageRow.isUpcoming ? baseWageRow : null), [
    baseWageRow
  ]);

  const existsHourlyWageInBaseWage = currentBaseWage?.hourlyWage > 0;

  const wageFormatter = useCallback(
    ({ value, rowData, key, currency = true }) => {
      return wageCellFormatter({
        value,
        rowData,
        t,
        key,
        currency,
        isFlatWage,
        formattingMap,
        mappedPositions,
        showDefaultWages: !existsHourlyWageInBaseWage
      });
    },
    [formattingMap, isFlatWage, existsHourlyWageInBaseWage, mappedPositions, t]
  );

  const columns = useMemo(
    () => [
      {
        Header: '',
        dataKey: 'positionId',
        Cell: PositionRenderer,
        columnData: { mappedPositions },
        captureRowClick: false
      },
      {
        Header: t('hourlyRate'),
        dataKey: 'hourlyWage',
        formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'hourlyWage' })
      },
      isFlatWage && {
        Header: t('flatWage'),
        dataKey: 'flatWage',
        formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'flatWage' })
      },
      isFlatWage && {
        Header: t('flatHours'),
        dataKey: 'flatHours',
        formatter: (value, rowData) => wageFormatter({ value, rowData, key: 'flatHours', currency: false })
      },
      {
        Header: t('effectiveFrom'),
        dataKey: 'effectiveDate',
        formatter: (value) => (value ? moment(value).format(dateFormat) : '-')
      }
    ],
    [dateFormat, isFlatWage, mappedPositions, t, wageFormatter]
  );

  const groupedWages = useMemo(() => groupWages({ positions, data }), [positions, data]);

  const groupedWagesByPositionId = useMemo(() => {
    return groupedWages.reduce((output, current) => {
      output[current.positionId] = current;
      return output;
    }, {});
  }, [groupedWages]);

  const allPositionsPartOfGroup = useMemo(() => positionsGroups.flatMap(({ positionsIds }) => positionsIds ?? []), [
    positionsGroups
  ]);

  const groupedPositionsWagesData = useMemo(
    () =>
      positionsGroups.reduce((output, current) => {
        const { positionsIds = [] } = current;
        const currentPositionsIds = positionsIds.filter((id) => positions.includes(id));

        if (currentPositionsIds.length === 0) {
          return output;
        }

        if (!output[current.id]) {
          output[current.id] = { drawerLabel: current.name, tableData: [] };
        }
        currentPositionsIds.forEach((positionId) => {
          output[current.id].tableData.push(groupedWagesByPositionId?.[positionId] || {});
        });

        return output;
      }, {}),
    [positionsGroups, positions, groupedWagesByPositionId]
  );

  const groupedWagesByPositionIdWithoutGroup = useMemo(
    () => groupedWages.filter(({ positionId }) => !allPositionsPartOfGroup.includes(positionId)),
    [groupedWages, allPositionsPartOfGroup]
  );

  const groupedData = useMemo(
    () => [
      { tableData: [baseWageRow, ...groupedWagesByPositionIdWithoutGroup] },
      ...Object.values(groupedPositionsWagesData)
    ],
    [baseWageRow, groupedWagesByPositionIdWithoutGroup, groupedPositionsWagesData]
  );

  const options = (newPositionsWages) => [
    { label: t('editPositionsGroupWage'), onClick: () => setEditedPositionGroupWage(newPositionsWages) }
  ];

  const onPositionsGroupWageChange = (newValue) => {
    const toEditWageIds = editedPositionsGroupWage.filter(({ id }) => !!id).map(({ id }) => id);
    const toAddWageIds = editedPositionsGroupWage.filter(({ id }) => !id);

    const newData = (toAddWageIds || []).map((item) => ({ id: -Date.now(), ...item, status: 'added' }));

    onChangePositionsWages(
      [...positionsWages, ...newData].reduce((all, item) => {
        if (toEditWageIds.includes(item.id)) {
          all.push({ ...item, ...newValue, status: item.status || 'edited' });
          return all;
        }

        all.push(item);

        return all;
      }, [])
    );
  };

  const handleRowClick = useCallback(
    ({ rowData, rowData: { id } }) => (!id ? handleAddWage(rowData) : handleEditWage(rowData)),
    [handleAddWage, handleEditWage]
  );

  return (
    <RootWrapper>
      <RootContainer>
        {!isPayRatesEnabled && (
          <Accordion defaultOpenRows={{ 0: true }}>
            <Accordion.Item title={t('hourlyWages')}>
              <AccordionContent>
                {(groupedData || []).map((currData, index) => {
                  const { drawerLabel, tableData = [] } = currData;
                  const defaultExtendsRows = tableData.length <= 2 ? { 0: true } : { 0: false };
                  return (
                    <div key={index}>
                      {drawerLabel && (
                        <PositionsGroupsTitle>
                          <SmallCategory>{`${drawerLabel} ${t('positionsGroup')}`}</SmallCategory>
                          {hasAmendPayRatePrivilege && <ContextMenu options={options(tableData)} />}
                        </PositionsGroupsTitle>
                      )}
                      <Table
                        isLoading={isLoading}
                        columns={columns}
                        defaultExtendsRows={defaultExtendsRows}
                        data={tableData}
                        rowStyle={({ wageStatus, isUpcoming }) => wageStatus || (isUpcoming ? 'future' : undefined)}
                        onRowClick={hasAmendPayRatePrivilege ? handleRowClick : null}
                        actionRenderer={({ rowData }) =>
                          hasAmendPayRatePrivilege && (
                            <WageActionRenderer
                              rowData={rowData}
                              columnData={{
                                handleAdd: handleAddWage,
                                handleDelete: handleDeleteWage,
                                handleEdit: handleEditWage
                              }}
                            />
                          )
                        }
                        shadow={false}
                      />
                    </div>
                  );
                })}
              </AccordionContent>
            </Accordion.Item>
          </Accordion>
        )}
        {editedWage && (
          <WageModal
            wage={editedWage}
            onClose={() => setEditedWage()}
            onConfirm={({
              subRows: _subRows,
              wageStatus: _wageStatus,
              isBaseWage: _isBaseWage,
              isUpcoming: _isUpcoming,
              ...wage
            }) => {
              const [edit, add] = editedWage.isBaseWage
                ? [handleBaseWageEdit, handleBaseWageAdd]
                : [handleEdit, handleAdd];
              if (wage.id) {
                edit(wage.id, wage);
              } else {
                add(wage);
              }
              setEditedWage();
            }}
            baseWage={editedWage.isBaseWage ? undefined : currentBaseWage}
          />
        )}
        {editedPositionsGroupWage && (
          <PositionsGroupWageModal
            positionsWages={editedPositionsGroupWage}
            onClose={() => setEditedPositionGroupWage()}
            onConfirm={onPositionsGroupWageChange}
          />
        )}

        <Accordion defaultOpenRows={{ 0: true }}>
          <Accordion.Item title={t('payrollFields')}>
            <AccordionContent>
              <FieldsContainer>
                {payrollFieldsByEmployee.map(({ id, slug, defaultValue }) => (
                  <InputField
                    key={id}
                    name={`payrollFields.field-${id}`}
                    title={t(slug)}
                    placeholder={`${t('default')} - ${defaultValue}`}
                  />
                ))}
              </FieldsContainer>
            </AccordionContent>
          </Accordion.Item>
        </Accordion>
      </RootContainer>
    </RootWrapper>
  );
};

export default React.memo(Wages);
